import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '../storage/storage.service';
import { catchError, forkJoin, Observable, of, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { ListQuest } from '../../../shared/services/lists/list-quest';
import { ListGender } from '../../../shared/services/lists/list-gender';
import { AllStatus } from '../status/all-status';
import { ListRoles } from '../../../shared/services/lists/list-roles';
import { FieldBuilder } from '../../../shared/services/fields/field-builder';

interface Language{
    name: string,
    disabled?: boolean
}

interface LanguageGroup{
    disabled?: boolean,
    name: string,
    languages: Language[]
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  translateService: TranslateService = inject(TranslateService);
  
  storageService: StorageService = inject(StorageService);
  fieldBuilder = inject(FieldBuilder);
  allStatus = inject(AllStatus);
  router = inject(Router);
  quests = inject(ListQuest);
  listRoles = inject(ListRoles);
  genders = inject(ListGender);
  public langShow: boolean = false;
    public appLanguage: string | undefined;
    private readonly STORAGE_KEY = "app_language";
    public readonly supportedLanguages = [ "uk", "ru", "en"];
    // public readonly languageGroup: LanguageGroup[] = [
    //     {
    //         name: 'Actual',
    //         languages:[
    //             {name: "uk"},
    //             {name: "ru"},
    //             {name: "en"}
    //         ]
    //     },{
    //         name: "Coming",
    //         languages:[
    //             {name: "de", disabled: true},
    //             {name: "es", disabled: true},
    //             {name: "pl", disabled: true}
    //         ]
    //     }
    //     ,{
    //         disabled: true,
    //         name: "Perspective",
    //         languages:[
    //             {name: "nl"},
    //             {name: "is"},
    //             {name: "it"},
    //             {name: "pt"},
    //             {name: "tr"},
    //             {name: "fr"}
    //         ]
    //     }
    // ];
    public readonly languageGroup: LanguageGroup[] = [
        {
            name: 'Actual',
            languages:[
                {name: "ru"},
                {name: "en"},
                {name: "uk"}
            ]
        }
    ]
    public readonly supportedFullNamesLanguages = {
        de: "German",
        en: "English",
        ar: "Arabic",
        bg: "Bulgarian",
        zh: "Chinese",
        hr: "Croatian",
        cs: "Czech",
        da: "Danish",
        nl: "Dutch",
        fi: "Finnish",
        fr: "French",
        hu: "Hungarian",
        it: "Italian",
        ja: "Japanese",
        lt: "Lithuanian",
        pl: "Polish",
        pt: "Portuguese",
        ro: "Romanian",
        ru: "Russian",
        uk: "Ukraine",
        sr: "Serbian",
        sk: "Slovak",
        sl: "Slovene",
        es: "Spanish",
        tr: "Turkish"
    };
    txtDialog: any;

  constructor() { 
    this.translateService.get(['spinner']).subscribe(translations => {
      this.txtDialog = translations['spinner'];
    });
  }

   public setLanguage(language: string): void {
        
        //this.mlmOnboardingService.currentLang = language;
        let isRefreshingGameData = false;
        this.storageService.set(this.STORAGE_KEY, language).pipe(
            switchMap(() => {
                this.appLanguage = language;

                const isWaiting = this.router.url.includes('waiting');
                const isLobby = this.router.url.includes('lobby');
                isRefreshingGameData = isWaiting || isLobby;

                if(isRefreshingGameData){
                    this.allStatus.textSpinner = (this.appLanguage === 'ru') ? 'Смена языка...' : (this.appLanguage === 'uk') ? 'Зміна мови...' : 'Changing language...';
                    this.allStatus.setDisplayProgressSpinner(true);
                    sessionStorage.removeItem("questsAll");

                    const typeGame = JSON.parse(sessionStorage.getItem("typeGame") as string);
                    const storedQuestInfo = sessionStorage.getItem("QuestInfo");
                    const questInfo = storedQuestInfo ? JSON.parse(storedQuestInfo) : null;
                    const listRefresh$ = typeGame === 'role'
                        ? this.listRoles.getRolesForLanguageChange()
                        : isLobby
                            ? this.quests.getAllQuestsByLobby()
                            : this.quests.getChangeLanguageAllQuests();
                    const detailRefresh$ = questInfo
                        ? typeGame === 'role'
                            ? this.listRoles.roleInfo(questInfo.rq_id)
                            : this.quests.questInfo(questInfo.rq_id)
                        : of(null);
                    const fieldRefresh$ = isLobby
                        ? this.fieldBuilder.loadPlayingFieldForLanguageChange([1])
                        : of(null);

                    if(typeGame === 'role'){
                        this.listRoles.roles = [];
                    }else{
                        this.quests.quests = [];
                    }

                    if(isWaiting && typeGame === 'role'){
                        this.allStatus.setSelectRQ(0);
                    }

                    return forkJoin([
                        this.updateLanguage$(),
                        listRefresh$,
                        detailRefresh$,
                        this.genders.loadingForLanguageChange(),
                        fieldRefresh$
                    ]);
                }

                return this.updateLanguage$();
            })
        ).subscribe({
            next: () => {
                this.allStatus.setChangeLanguage(true);
                if(isRefreshingGameData){
                    this.allStatus.setDisplayProgressSpinner(false);
                }
            },
            error: (error) => {
                console.error('Failed to refresh data after changing language', error);
                if(isRefreshingGameData){
                    this.allStatus.setDisplayProgressSpinner(false);
                }
            }
        });
    }

    public catchAppLanguage$(): Observable<void> {
        this.storageService.get(this.STORAGE_KEY).pipe(
            switchMap((language: string) => {
                this.appLanguage = language || this.translateService.getBrowserLang();

                return this.translateService.use(<string>this.appLanguage).pipe(
                    catchError((err: any) => {
                        // this.appLanguage = "ru";
                        console.error("Error is use app language", err);

                        return of(err);
                    })
                );
            }),
            catchError(() => {

                this.appLanguage = this.translateService.getBrowserLang();

                return of(undefined);
            })
        ).subscribe();


        if (this.supportedLanguages.indexOf(<string>this.appLanguage) === -1) {
            this.appLanguage = "ru";
        }
        this.storageService.set(this.STORAGE_KEY, this.appLanguage);
        return this.updateLanguage$();
    }

    private updateLanguage$(): Observable<void> {
        return this.translateService.use(<string>this.appLanguage).pipe(
            catchError((err: any) => {
                // this.appLanguage = "ru";
                console.error("Error is use app language", err);

                return of(err);
            })
        );
    }
}
